import React from 'react';
import { makeStyles, Button, Text } from '@fluentui/react-components';
import { CopyRegular } from '@fluentui/react-icons';
import { JSONTree } from 'react-json-tree';

interface JsonViewerProps {
    data: any;
    error?: string | null;
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        width: '100%',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '8px',
    },
    viewerContainer: {
        maxHeight: '500px',
        overflow: 'auto',
        backgroundColor: '#1e1e1e',
        borderRadius: '4px',
        padding: '12px',
        border: '1px solid #6B7584',
        fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
        fontSize: '13px',
    },
    errorContainer: {
        maxHeight: '500px',
        overflow: 'auto',
        backgroundColor: '#3A1E1E',
        borderRadius: '4px',
        padding: '12px',
        border: '1px solid #8B2E2E',
        color: '#FFB3B3',
    },
    emptyState: {
        padding: '24px',
        textAlign: 'center',
        color: '#8A8A8A',
        fontStyle: 'italic',
    },
    copyButton: {
        minWidth: 'auto',
    },
    pre: {
        margin: 0,
        fontFamily: "'Fira Mono', Consolas, 'Courier New', monospace",
        fontSize: '13px',
        lineHeight: '1.5',
    },
});

const JsonViewer: React.FC<JsonViewerProps> = ({ data, error }) => {
    const classes = useStyles();
    const [copySuccess, setCopySuccess] = React.useState(false);

    const jsonString = React.useMemo(() => {
        if (data === null || data === undefined) return '';
        try {
            return JSON.stringify(data, null, 2);
        } catch (e) {
            return String(data);
        }
    }, [data]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(jsonString);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // If there's an error, display it
    if (error) {
        return (
            <div className={classes.container}>
                <div className={classes.header}>
                    <Text weight="semibold" style={{ color: '#FFB3B3' }}>
                        Error
                    </Text>
                </div>
                <div className={classes.errorContainer}>
                    <pre className={classes.pre}>{error}</pre>
                </div>
            </div>
        );
    }

    // If no data, show empty state
    if (!data) {
        return (
            <div className={classes.container}>
                <div className={classes.header}>
                    <Text weight="semibold">Response</Text>
                </div>
                <div className={classes.viewerContainer}>
                    <div className={classes.emptyState}>
                        No response yet. Execute a debug operation to see results here.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <Text weight="semibold">Response</Text>
                <Button
                    appearance="subtle"
                    icon={<CopyRegular />}
                    size="small"
                    className={classes.copyButton}
                    onClick={handleCopy}
                >
                    {copySuccess ? 'Copied!' : 'Copy'}
                </Button>
            </div>
            <div className={classes.viewerContainer}>
                <JSONTree
                    data={data}
                    theme={{
                        scheme: 'monokai',
                        author: 'wimer hazenberg (http://www.monokai.nl)',
                        base00: '#1e1e1e',
                        base01: '#383830',
                        base02: '#49483e',
                        base03: '#75715e',
                        base04: '#a59f85',
                        base05: '#f8f8f2',
                        base06: '#f5f4f1',
                        base07: '#f9f8f5',
                        base08: '#f92672',
                        base09: '#fd971f',
                        base0A: '#f4bf75',
                        base0B: '#a6e22e',
                        base0C: '#a1efe4',
                        base0D: '#66d9ef',
                        base0E: '#ae81ff',
                        base0F: '#cc6633',
                    }}
                    invertTheme={false}
                    hideRoot={false}
                    shouldExpandNodeInitially={(keyPath: readonly (string | number)[], data: any, level: number) => level < 3}
                />
            </div>
        </div>
    );
};

export default JsonViewer;